<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useOrganizationStore } from '@/stores/organizationStore'

const route = useRoute()
const organizationId = route.params.id
const orgStore = useOrganizationStore()

// State
const isLoading = ref(false)
const isCreating = ref(false)
const newTeamName = ref('')
const errorMsg = ref('')

// Access teams from store
const teams = computed(() => orgStore.teams)

// Fetch Teams on load
const fetchTeams = async () => {
  isLoading.value = true
  try {
    await orgStore.fetchTeams(organizationId)
  } catch (err) {
    console.error('Failed to fetch teams:', err)
  } finally {
    isLoading.value = false
  }
}

// Create Team Handler
const handleCreateTeam = async () => {
  if (!newTeamName.value.trim()) return

  isCreating.value = true
  errorMsg.value = ''

  try {
    await orgStore.createTeam(organizationId, newTeamName.value)
    // Clear input on success
    newTeamName.value = ''
  } catch (err) {
    errorMsg.value = err.response?.data?.error?.message || 'Failed to create team.'
  } finally {
    isCreating.value = false
  }
}

onMounted(() => {
  fetchTeams()
})
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Teams</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage teams and team members</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
        <h2 class="text-lg text-gray-900">Teams</h2>
        <span v-if="isLoading" class="text-xs text-gray-400">Loading...</span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-gray-100">
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Name</th>
              <th class="px-6 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="teams.length === 0 && !isLoading">
              <td colspan="2" class="px-6 py-8 text-center text-gray-500">
                No teams found. Create one below.
              </td>
            </tr>
            <tr v-for="team in teams" :key="team.id"
              class="bg-white hover:bg-gray-50/50 transition border-b border-gray-50 last:border-0">
              <td class="px-6 py-2 font-medium text-gray-900">{{ team.name }}</td>
              <td class="px-6 py-2 text-right">
                <div class="inline-flex items-center gap-1">
                  <router-link :to="`/organizations/${organizationId}/teams/${team.id}`"
                    class="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition"
                    title="Manage team members and settings">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                    </svg>
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-4 bg-gray-50 border-t border-gray-100">
        <p v-if="errorMsg" class="mb-2 text-xs text-red-600 font-medium px-1">{{ errorMsg }}</p>

        <form @submit.prevent="handleCreateTeam" class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <input v-model="newTeamName" type="text" placeholder="Team Name" :disabled="isCreating"
            class="grow px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black shadow-sm h-9 disabled:bg-gray-100 disabled:text-gray-500" />

          <button type="submit" :disabled="isCreating || !newTeamName.trim()"
            class="bg-black text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition flex items-center h-9 disabled:opacity-70 disabled:cursor-not-allowed">
            <svg v-if="isCreating" class="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            <svg v-else class="w-3.5 h-3.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4" />
            </svg>
            Create team
          </button>
        </form>
      </div>
    </div>
  </div>
</template>